import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { identifyUser, resetUser } from './analytics/posthog';

export type AppMode = 'demo' | 'admin';
export type UserRole = 'super_admin' | 'client_admin' | 'client_analyst' | 'reviewer' | 'admin' | 'administrator';

interface AdminContextType {
  adminMode: boolean;
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  maskClientName: (name: string, index?: number) => string;
  logout: () => void;
  viewAsClient: string | null;
  setViewAsClient: (client: string | null) => void;
  modeVersion: number;
  userRole: UserRole | null;
  userClientName: string | null;
  isSuperAdmin: boolean;
  setUserContext: (role: UserRole, clientName: string | null) => void;
}

const AdminContext = createContext<AdminContextType>({
  adminMode: false,
  mode: 'demo',
  setMode: () => {},
  maskClientName: () => '',
  logout: () => {},
  viewAsClient: null,
  setViewAsClient: () => {},
  modeVersion: 0,
  userRole: null,
  userClientName: null,
  isSuperAdmin: false,
  setUserContext: () => {},
});

const STORAGE_KEY = 'meridian_app_mode';
const CLIENT_VIEW_KEY = 'meridian_view_as_client';
const USER_ROLE_KEY = 'meridian_user_role';
const USER_CLIENT_KEY = 'meridian_user_client';

interface JwtPayload {
  sub?: string;
  email?: string;
  role?: string;
  client_name?: string | null;
  exp?: number;
  iss?: string;
  aud?: string | string[];
  is_admin?: boolean;
  name?: string;
  user_id?: string;
  [key: string]: unknown;
}

function parseJwt(token: string): JwtPayload | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

export function AdminProvider({ children }: { children: ReactNode }) {
  // Restore user context from JWT on mount
  // Auth0 tokens may not contain role/client_name — fall back to localStorage
  const [userRole, setUserRole] = useState<UserRole | null>(() => {
    try {
      const token = sessionStorage.getItem('meridian_token');
      if (token) {
        const payload = parseJwt(token);
        // Token is valid if it has an exp in the future, or if it's an Auth0 token (has 'iss' or 'aud')
        const isValid = payload && (
          (payload.exp && payload.exp * 1000 > Date.now()) ||
          payload.iss || payload.aud
        );
        if (isValid) {
          return (payload.role as UserRole) || (localStorage.getItem(USER_ROLE_KEY) as UserRole) || null;
        }
      }
      // No valid token — still check localStorage for persisted role from previous setUserContext
      return (localStorage.getItem(USER_ROLE_KEY) as UserRole) || null;
    } catch { return null; }
  });

  const [userClientName, setUserClientName] = useState<string | null>(() => {
    try {
      const token = sessionStorage.getItem('meridian_token');
      if (token) {
        const payload = parseJwt(token);
        const isValid = payload && (
          (payload.exp && payload.exp * 1000 > Date.now()) ||
          payload.iss || payload.aud
        );
        if (isValid) {
          return payload.client_name || localStorage.getItem(USER_CLIENT_KEY) || null;
        }
      }
      return localStorage.getItem(USER_CLIENT_KEY) || null;
    } catch { return null; }
  });

  const isSuperAdmin = userRole === 'super_admin';

  const [mode, setModeState] = useState<AppMode>(() => {
    try {
      const token = sessionStorage.getItem('meridian_token');
      if (token) {
        const payload = parseJwt(token);
        if (payload && payload.exp && payload.exp * 1000 > Date.now()) {
          // Client users always see their client view
          if (payload.client_name) return 'admin'; // admin mode with viewAsClient
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored === 'admin' || stored === 'demo') return stored;
          return 'admin';
        }
        sessionStorage.removeItem('meridian_token');
      }
      return 'demo';
    } catch { return 'demo'; }
  });

  const [modeVersion, setModeVersion] = useState(0);

  const [viewAsClient, setViewAsClientState] = useState<string | null>(() => {
    try {
      // Client users auto-enter view-as-client for their assigned client
      const token = sessionStorage.getItem('meridian_token');
      if (token) {
        const payload = parseJwt(token);
        if (payload && payload.client_name) return payload.client_name;
      }
      return localStorage.getItem(CLIENT_VIEW_KEY) || null;
    } catch { return null; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, mode); } catch {}
  }, [mode]);

  useEffect(() => {
    try {
      if (viewAsClient) localStorage.setItem(CLIENT_VIEW_KEY, viewAsClient);
      else localStorage.removeItem(CLIENT_VIEW_KEY);
    } catch {}
  }, [viewAsClient]);

  useEffect(() => {
    try {
      if (userRole) localStorage.setItem(USER_ROLE_KEY, userRole);
      else localStorage.removeItem(USER_ROLE_KEY);
      if (userClientName) localStorage.setItem(USER_CLIENT_KEY, userClientName);
      else localStorage.removeItem(USER_CLIENT_KEY);
    } catch {}
  }, [userRole, userClientName]);

  const setMode = (m: AppMode) => {
    // Client users cannot switch to admin mode
    if (!isSuperAdmin && userClientName && m === 'admin') return;
    setModeState(m);
    if (m === 'demo') setViewAsClientState(null);
    setModeVersion(v => v + 1);
  };

  const adminMode = mode === 'admin' && !viewAsClient && isSuperAdmin;

  const setViewAsClient = (client: string | null) => {
    // Client users cannot exit their client view
    if (!isSuperAdmin && userClientName) return;
    setViewAsClientState(client);
    if (client) setModeState('admin');
    if (!client) {
      try { localStorage.removeItem(CLIENT_VIEW_KEY); } catch {}
    }
    setModeVersion(v => v + 1);
  };

  const setUserContext = (role: UserRole, clientName: string | null) => {
    setUserRole(role);
    setUserClientName(clientName);
    if (clientName) {
      // Client user: auto-enter their client view
      setViewAsClientState(clientName);
      setModeState('admin');
    } else {
      // Super admin: restore saved mode
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'admin' || stored === 'demo') setModeState(stored);
      else setModeState('admin');
    }
    setModeVersion(v => v + 1);

    // Identify user in Sentry + PostHog
    try {
      const token = sessionStorage.getItem('meridian_token');
      const payload = token ? parseJwt(token) : null;
      const email = payload?.email || payload?.sub || 'unknown';
      Sentry.setUser({ email, role, client_name: clientName });
      identifyUser(String(email), { role, client_name: clientName });
    } catch { /* non-critical */ }
  };

  const maskClientName = (name: string, index?: number) => {
    if (mode === 'admin' && !viewAsClient) return name;
    if (index !== undefined) return `Client ${index + 1}`;
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = ((hash << 5) - hash) + name.charCodeAt(i);
      hash |= 0;
    }
    return `Client ${Math.abs(hash % 900) + 100}`;
  };

  const logout = () => {
    sessionStorage.removeItem('meridian_token');
    setModeState('demo');
    setViewAsClientState(null);
    setUserRole(null);
    setUserClientName(null);
    setModeVersion(v => v + 1);
    try {
      localStorage.setItem(STORAGE_KEY, 'demo');
      localStorage.removeItem(CLIENT_VIEW_KEY);
      localStorage.removeItem(USER_ROLE_KEY);
      localStorage.removeItem(USER_CLIENT_KEY);
    } catch {}
    try {
      Sentry.setUser(null);
      resetUser();
    } catch { /* non-critical */ }
  };

  return (
    <AdminContext.Provider value={{
      adminMode, mode, setMode, maskClientName, logout,
      viewAsClient, setViewAsClient, modeVersion,
      userRole, userClientName, isSuperAdmin, setUserContext,
    }}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  return useContext(AdminContext);
}
